import React, { useMemo } from 'react';
import { nodeCardHeight, NODE_CARD_WIDTH } from './nodeCardMetrics';
import { loopColor, loopTone, loopWord } from './loopVisuals';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * LoopEdgeLayer — the closing edge, drawn the way it was drawn.
 *
 * A back-edge cannot be laid out: the tree has no column for it. So it is drawn
 * as what it is — a return arc that leaves the source card's handle, rises over
 * the map and comes down into the top edge of the box it points back at. It
 * touches nothing else's position (the anti-reflow rule), which is exactly the
 * freedom an overlay has and a spine edge does not.
 *
 * It carries the loop's word — noidankehä, hyvä kierre — not a link type,
 * because a loop is not a consequence: what the room needs to know about it is
 * which way it runs, and that is a judgement about the content, not about the
 * arrows. The pill is also the way into the loop's description.
 */

/** Where the arrow lands on the target's top edge — a quarter of the way in
 *  from the side the arc arrives from, so it reads as landing on the box and
 *  does not come down on top of that box's own handle. */
const ENTRY_INSET = NODE_CARD_WIDTH * 0.25;

/** Corner radius on the orthogonal run. */
const CORNER = 18;

/** A rounded corner between two axis-aligned runs, as a path fragment. */
function corner(x, y, inDx, inDy, outDx, outDy, r) {
  return `L ${x - inDx * r} ${y - inDy * r} Q ${x} ${y} ${x + outDx * r} ${y + outDy * r}`;
}

/**
 * Out of the source's handle into the gap beside it, up that gap to the
 * channel, along the channel, down the gap beside the target and in onto the
 * target's top edge: straight runs and rounded corners. A curve looked like a
 * decoration draped over the map; a right-angled run reads as a route, which
 * is what it is. It travels in the gaps between columns and the band above
 * the row, so it goes round the cards of its ring rather than through them.
 */
function loopPath({ sx, sy, riseX, channelY, dropX, tx, ty }) {
  const pts = [
    [sx, sy],
    [riseX, sy],
    [riseX, channelY],
    [dropX, channelY],
    [dropX, ty - 14],
    [tx, ty - 14],
    [tx, ty],
  ];
  // Drop zero-length runs (a riser that already sits over the entry point).
  const run = pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1][0] || p[1] !== pts[i - 1][1]);
  let d = `M ${run[0][0]} ${run[0][1]}`;
  for (let i = 1; i < run.length - 1; i += 1) {
    const [px, py] = run[i - 1]; const [x, y] = run[i]; const [nx, ny] = run[i + 1];
    const inLen = Math.hypot(x - px, y - py); const outLen = Math.hypot(nx - x, ny - y);
    const r = Math.min(CORNER, inLen / 2, outLen / 2);
    if (r < 1) { d += ` L ${x} ${y}`; continue; }
    d += ` ${corner(x, y, (x - px) / inLen, (y - py) / inLen, (nx - x) / outLen, (ny - y) / outLen, r)}`;
  }
  const [lx, ly] = run[run.length - 1];
  d += ` L ${lx} ${ly}`;
  return d;
}

export default function LoopEdgeLayer({
  loops = [],
  connections = [],
  nodes = [],
  positions = {},
  // Per loop id: the y of the clear horizontal band the layout reserved for it.
  channels = {},
  onLoopClick,
  selection = null,
  onSelect = null,
}) {
  const { t } = useTranslation();
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const drawn = useMemo(() => {
    return loops
      .map((loop) => {
        const from = positions[loop.closingFromId];
        const to = positions[loop.closingToId];
        if (!from || !to) return null;

        const fromHeight = nodeCardHeight(nodeById.get(loop.closingFromId));
        const toHeight = nodeCardHeight(nodeById.get(loop.closingToId));

        // Leaves the source's handle, lands on the target's top edge — the same
        // two points the drag used, so the arc reads as the gesture that made
        // it. Between them it keeps to the gaps the layout reserved.
        const channel = channels[loop.id];
        const channelY = channel?.y ?? (Math.min(from.y - fromHeight / 2, to.y - toHeight / 2) - 60);
        const sx = from.x + NODE_CARD_WIDTH / 2;
        const sy = from.y;
        const riseX = channel?.riseX ?? (sx + 40);
        const dropX = channel?.dropX ?? (to.x - NODE_CARD_WIDTH / 2 - 40);
        const ty = to.y - toHeight / 2;
        const tx = to.x + (dropX >= to.x ? ENTRY_INSET : -ENTRY_INSET);
        const path = loopPath({ sx, sy, riseX, channelY, dropX, tx, ty });
        // The pill rides the horizontal run, where there is room.
        const midX = (riseX + dropX) / 2;
        const midY = channelY;

        const color = loopColor(loop);

        return {
          loop,
          path,
          midX,
          midY,
          tx,
          ty,
          color,
          // The pill says what the loop *is* — noidankehä or hyvä kierre — in
          // the loop's colour, and until somebody has judged it, just that it
          // is a loop. Not the closing link's type: a loop differs from a
          // consequence. A consequence has a type; a loop has a direction.
          typeColor: color,
          label: t(`impactMapV2.loop.word.${loopWord(loop)}`),
          selectKey: `${loop.closingFromId}->${loop.closingToId}`,
          ids: loop.nodeIds?.length ? loop.nodeIds : [loop.closingFromId, loop.closingToId],
        };
      })
      .filter(Boolean);
  }, [loops, connections, positions, nodeById, t]);

  if (!drawn.length) return null;

  return (
    <g className="loop-edge-layer">
      <defs>
        {drawn.map((d) => (
          <marker
            key={`marker-${d.loop.id}`}
            id={`loop-arrow-${d.loop.id}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={d.color} />
          </marker>
        ))}
      </defs>

      {drawn.map((d) => {
        const pillWidth = d.label.length * 8.2 + 26;
        return (
          <g
            key={d.loop.id}
            className="loop-edge"
            opacity={selection && selection.key !== d.selectKey ? 0.09 : 1}
            style={{ transition: 'opacity 0.18s ease' }}
          >
            {/* Tapping the ring lights the whole ring, not just its two ends —
                a loop is a claim about every node it runs through. */}
            {onSelect && (
              <path
                d={d.path}
                stroke="transparent"
                strokeWidth={18}
                fill="none"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({
                    key: d.selectKey,
                    ids: new Set(d.ids),
                    from: d.loop.closingFromId,
                    to: d.loop.closingToId,
                    // A loop is read as a ring, so focusing one lays out the
                    // whole chain rather than only the link that closes it.
                    chain: d.loop.nodeIds?.length ? d.loop.nodeIds : [d.loop.closingToId, d.loop.closingFromId],
                    loop: d.loop,
                    label: d.label,
                    color: d.color,
                  });
                }}
              />
            )}
            <path
              d={d.path}
              fill="none"
              stroke={d.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              markerEnd={`url(#loop-arrow-${d.loop.id})`}
              style={{ pointerEvents: 'none' }}
            />

            {/* The loop's word, on the arc — and the way into the loop. */}
            <g
              transform={`translate(${d.midX}, ${d.midY})`}
              style={{ cursor: onLoopClick ? 'pointer' : 'default' }}
              onClick={(e) => { e.stopPropagation(); onLoopClick?.(d.loop); }}
            >
              <title>{`${t(`impactMapV2.loop.tone.${loopTone(d.loop).key}`)}${d.loop.title ? ` — ${d.loop.title}` : ''}`}</title>
              <rect
                x={-pillWidth / 2}
                y={-13}
                width={pillWidth}
                height={26}
                rx={13}
                fill="#12121c"
                stroke={d.typeColor}
                strokeWidth={1.5}
              />
              <text
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={13}
                fontWeight={600}
                fill={d.typeColor}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {d.label}
              </text>
            </g>

            {d.loop.hasDelay && (
              /* The CLD delay mark: two strokes across the run, after the pill. */
              <g transform={`translate(${d.midX + pillWidth / 2 - 8}, ${d.midY})`} stroke={d.color} strokeWidth={2.5} strokeLinecap="round">
                <line x1={6} y1={-9} x2={12} y2={-15} />
                <line x1={11} y1={-4} x2={17} y2={-10} />
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
}
